import { useState } from 'react';

import { useRouter } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { calculateCartSubTotal } from 'features/cart/utils';
import { EnvItem } from 'features/diagnostics/components/env-item';
import { usePriceOrderV2Input } from 'features/order/hooks/use-price-order-v2-input';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { BottomSheet } from 'components/bottom-sheet';
import { Footer, InlineModal } from 'components/ui';
import { IMenuSelectionInput } from 'generated/graphql-gateway';
import { routes } from 'navigation/routing';

import { CartDebugActionButton } from './cart-debug-action-button';
import { CartDebugManageDonationsModal } from './cart-debug-manage-donations-modal';
import { CartDebugManageUtensilsModal } from './cart-debug-manage-utensils-modal';
import { CartDebugSubtotalModal } from './cart-debug-subtotal-modal';

const deriveMenuSelections = (
  menuSelections: readonly IMenuSelectionInput[]
): IMenuSelectionInput[] => {
  return menuSelections.map(selection => ({
    ...selection,
    menuSelections: deriveMenuSelections([...(selection.menuSelections ?? [])]),
  }));
};

const EnvItemModal = ({
  isOpen,
  onClose,
  envKey,
  value,
}: {
  isOpen: boolean;
  onClose: () => void;
  envKey: string;
  value: string;
}) => {
  if (isOpen) {
    // eslint-disable-next-line no-console
    console.log({ [envKey]: JSON.parse(value) });
  }

  return (
    <InlineModal
      isOpen={isOpen}
      onClose={onClose}
      header={{ title: `Debug ${envKey}`, variant: 'light' }}
    >
      <ScrollView>
        <EnvItem envKey={envKey} value={value} />
      </ScrollView>
    </InlineModal>
  );
};

export const CartDebugFooter = () => {
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isUtensilsOpen, setIsUtensilsOpen] = useState(false);
  const [isDonationsOpen, setIsDonationsOpen] = useState(false);
  const [isMinimalCartDebugOpen, setIsMinimalCartDebugOpen] = useState(false);
  const [isFullCartDebugOpen, setIsFullCartDebugOpen] = useState(false);
  const [isPriceOrderInputDebugOpen, setIsPriceOrderInputDebugOpen] = useState(false);
  const [isSubTotalDebugOpen, setIsSubTotalDebugOpen] = useState(false);
  const { emptyMinimalCart } = useMinimalCart();
  const { cartEntries } = useMinimalCart();
  const { navigate } = useRouter();

  const menuSelections = cartEntries.map(
    entry =>
      ({
        id: entry.id,
        quantity: entry.quantity,
        price: entry.price,
        menuSelections: deriveMenuSelections(entry.menuSelections ?? []),
      } as IMenuSelectionInput)
  );

  const subTotal = calculateCartSubTotal(cartEntries);
  const priceOrderInput = usePriceOrderV2Input(menuSelections);

  const handleViewActions = (type: 'viewMinimalCart' | 'viewPriceOrderInput' | 'viewFullCart') => {
    setIsViewOpen(false);

    setTimeout(() => {
      if (type === 'viewMinimalCart') {
        setIsMinimalCartDebugOpen(true);
      }

      if (type === 'viewPriceOrderInput') {
        setIsPriceOrderInputDebugOpen(true);
      }
      if (type === 'viewFullCart') {
        setIsFullCartDebugOpen(true);
      }
    }, 300);
  };

  return (
    <Footer style={debugStyles.footer}>
      <View style={debugStyles.contentColumn}>
        <Text style={debugStyles.subtotalText}>SubTotal: {subTotal}</Text>
        <View style={debugStyles.buttonRow}>
          <CartDebugActionButton onPress={() => setIsSubTotalDebugOpen(true)}>
            Debug subTotal
          </CartDebugActionButton>
          <CartDebugActionButton onPress={() => emptyMinimalCart()}>
            Clear Cart
          </CartDebugActionButton>
          <CartDebugActionButton onPress={() => setIsViewOpen(true)}>View...</CartDebugActionButton>
          <CartDebugActionButton onPress={() => setIsAddOpen(true)}>Add...</CartDebugActionButton>
        </View>
      </View>

      <BottomSheet
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        onOpenEventMessage="Cart Debug View Menu"
      >
        <View style={debugStyles.menuContainer}>
          <Pressable
            style={debugStyles.menuItem}
            onPress={() => handleViewActions('viewMinimalCart')}
          >
            <Text style={debugStyles.menuItemText}>View Minimal Cart</Text>
          </Pressable>
          <Pressable style={debugStyles.menuItem} onPress={() => handleViewActions('viewFullCart')}>
            <Text style={debugStyles.menuItemText}>View Full Cart</Text>
          </Pressable>
          <Pressable
            style={debugStyles.menuItem}
            onPress={() => handleViewActions('viewPriceOrderInput')}
          >
            <Text style={debugStyles.menuItemText}>View Price Order Input</Text>
          </Pressable>
        </View>
      </BottomSheet>

      <BottomSheet
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onOpenEventMessage="Cart Debug Add Menu"
      >
        <View style={debugStyles.menuContainer}>
          <Pressable
            style={debugStyles.menuItem}
            onPress={() => {
              navigate(routes.menu);
              setIsAddOpen(false);
            }}
          >
            <Text style={debugStyles.menuItemText}>Add Items</Text>
          </Pressable>
          <Pressable
            style={debugStyles.menuItem}
            onPress={() => {
              navigate(routes.offers);
              setIsAddOpen(false);
            }}
          >
            <Text style={debugStyles.menuItemText}>Add Offers</Text>
          </Pressable>
          <Pressable
            style={debugStyles.menuItem}
            onPress={() => {
              navigate(routes.rewards);
              setIsAddOpen(false);
            }}
          >
            <Text style={debugStyles.menuItemText}>Add Rewards</Text>
          </Pressable>
          <Pressable
            style={debugStyles.menuItem}
            onPress={() => {
              setIsAddOpen(false);
              setIsUtensilsOpen(true);
            }}
          >
            <Text style={debugStyles.menuItemText}>Add Utensils</Text>
          </Pressable>
          <Pressable
            style={debugStyles.menuItem}
            onPress={() => {
              setIsAddOpen(false);
              setIsDonationsOpen(true);
            }}
          >
            <Text style={debugStyles.menuItemText}>Add Donation</Text>
          </Pressable>
        </View>
      </BottomSheet>

      <EnvItemModal
        envKey={'Minimal cart'}
        value={JSON.stringify({ cart: menuSelections })}
        isOpen={isMinimalCartDebugOpen}
        onClose={() => setIsMinimalCartDebugOpen(false)}
      />
      <EnvItemModal
        envKey={'Full cart'}
        value={JSON.stringify({ cart: cartEntries })}
        isOpen={isFullCartDebugOpen}
        onClose={() => setIsFullCartDebugOpen(false)}
      />
      <EnvItemModal
        envKey={'Price Order Input'}
        value={JSON.stringify({ input: priceOrderInput })}
        isOpen={isPriceOrderInputDebugOpen}
        onClose={() => setIsPriceOrderInputDebugOpen(false)}
      />
      <CartDebugSubtotalModal
        cartEntries={cartEntries}
        isOpen={isSubTotalDebugOpen}
        onClose={() => setIsSubTotalDebugOpen(false)}
      />
      <CartDebugManageUtensilsModal
        isOpen={isUtensilsOpen}
        onClose={() => setIsUtensilsOpen(false)}
      />
      <CartDebugManageDonationsModal
        isOpen={isDonationsOpen}
        onClose={() => setIsDonationsOpen(false)}
      />
    </Footer>
  );
};

const debugStyles = StyleSheet.create({
  footer: {
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  contentColumn: {
    flexDirection: 'column',
    gap: 6,
  },
  subtotalText: {
    fontSize: 16,
    fontWeight: 'bold',
    alignSelf: 'center',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 6,
  },
  menuContainer: {
    width: '100%',
    paddingVertical: 8,
  },
  menuItem: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  menuItemText: {
    fontSize: 16,
  },
});
